var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Utils, PCSS, Transformer } from '@pacem/pacem-core';
import { Components as ComponentsDrawing } from '@pacem/pacem-2d';
const LOOP_CLR = ['pink', 'blue', 'orange', 'purple', 'green', 'yellow', 'brown', 'red', 'teal'];
const NOLOOP_CLR = 'disabled';
function buildStroke(loops, loop, normalizedLength) {
    if (loops) {
        const paletteColor = loop >= 0 ? LOOP_CLR[loop % LOOP_CLR.length] : NOLOOP_CLR;
        return Utils.Css.getVariableValue(`--pacem-color-${paletteColor}`);
    }
    else {
        var blue = 240, red = 0;
        const pct = blue - (normalizedLength * (blue - red));
        return `hsl(${Math.round(pct)}, 100%, 50%)`;
    }
}
function arrow(fill, size, memoizer) {
    const key = `${fill}_${size}`;
    if (!memoizer.has(key)) {
        memoizer.set(key, { fill, viewbox: { x: 0, y: 0, width: 10, height: 10 }, pathData: 'M 0 0 L 10 5 L 0 10 Z', ref: { x: 10, y: 5 }, width: size, height: size });
    }
    return memoizer.get(key);
}
const MULTIPLIER_RANGE_VALIDATOR = {
    type: 'range', attributes: (host, hostRef, entityRef) => {
        return { 'error-message': 'out of range', 'min': '2', 'max': `{{ Math.max(${entityRef}.modulus || 0, 2) }}` };
    }
};
export const VORTEX_METADATA = {
    display: { cssClass: [PCSS + '-grid', 'grid-nospace', 'form-minimal'] },
    props: [
        {
            prop: 'size', type: 'int', display: { name: 'stage size', cssClass: [PCSS + '-cell', 'hide-sx', 'hide-v', 'field-stretch', 'field-minimal'], ui: 'slider' },
            validators: [
                { type: 'range', errorMessage: 'out of range', params: { min: 200, max: 800 } }
            ]
        },
        {
            prop: 'modulus', type: 'int', display: { name: 'modulus', cssClass: [PCSS + '-cell', 'cols-xl-3', 'field-stretch', 'field-outline'] },
            validators: [
                { type: 'required', errorMessage: 'required' },
                { type: 'range', errorMessage: 'out of range', params: { min: 3, max: 10000 } }
            ]
        },
        {
            prop: 'modulus', type: 'int', display: { name: ' ', cssClass: [PCSS + '-cell', 'cols-xl-9', 'hide-sx', 'hide-v', 'field-stretch', 'field-minimal'], ui: 'slider' },
            validators: [
                { type: 'range', errorMessage: 'out of range', params: { min: 3, max: 10000 } }
            ]
        },
        {
            prop: 'multiplier', type: 'int', display: { name: 'multiplier', cssClass: [PCSS + '-cell', 'cols-xl-3', 'field-stretch', 'field-outline'] },
            validators: [
                { type: 'required', errorMessage: 'required' },
                MULTIPLIER_RANGE_VALIDATOR
            ]
        },
        {
            prop: 'multiplier', type: 'int', display: { name: ' ', cssClass: [PCSS + '-cell', 'cols-xl-9', 'hide-sx', 'hide-v', 'field-stretch', 'field-minimal'], ui: 'slider' },
            validators: [
                MULTIPLIER_RANGE_VALIDATOR
            ]
        },
        {
            prop: 'color', type: 'enumeration', display: { name: 'colorize', cssClass: [PCSS + '-cell', 'field-nowrap', 'field-outline'] },
            extra: { enum: [{ value: 'length', caption: 'by length' }, { value: 'loops', caption: 'loops' }] }
        },
        {
            prop: 'circle', type: 'boolean', display: { name: 'outer circle', cssClass: [PCSS + '-cell', 'cols-xl-4', 'field-outline'], ui: 'switcher', watermark: 'show outer circle' }
        },
        {
            prop: 'arrows', type: 'boolean', display: { name: 'arrows', cssClass: [PCSS + '-cell', 'cols-xl-4', 'field-outline'], ui: 'switcher', watermark: 'show arrows' }
        },
        {
            prop: 'webgl', type: 'boolean', display: { name: 'Canvas rendering', cssClass: [PCSS + '-cell', 'cols-xl-4', 'field-outline', PCSS + '-switcher', 'switcher-accent'], ui: 'switcher', watermark: 'WebGL rending' }
        }
    ]
};
export function getVortexMetadata(minimal) {
    if (!minimal) {
        return VORTEX_METADATA;
    }
    const retval = Utils.clone(VORTEX_METADATA);
    for (let j = retval.props.length - 1; j >= 0; j--) {
        const prop = retval.props[j];
        if (typeof prop.display.cssClass === 'function') {
            continue;
        }
        if (prop.display.cssClass.indexOf('hide-sx') >= 0) {
            retval.props.splice(j, 1);
        }
        do {
            const ndxCss = prop.display.cssClass.findIndex(i => i.startsWith('cols-'));
            if (ndxCss >= 0) {
                prop.display.cssClass.splice(ndxCss, 1);
            }
            else {
                break;
            }
        } while (true);
    }
    return retval;
}
class VortexTransformers {
    static vortexDatabind(stage, datasource, loops = false, arrows = false, circle = false) {
        const { center, radius, segments, anchors } = datasource;
        const ds = [];
        const memoizer = new Map();
        const orderOfMag = Math.log10(segments.length);
        const thickness = Math.pow(0.25, orderOfMag), opacity = 1.0;
        if (circle) {
            const stroke = Utils.Css.getVariableValue('--pacem-color-disabled');
            const bg = {
                stroke, fill: 'none', lineWidth: thickness, opacity: 0.5, pathData: ComponentsDrawing.Drawing.PacemCircleElement.getPathData(center, radius), inert: true
            };
            const circles = { fill: stroke, stroke: 'none', opacity: 0.5, pathData: '', inert: true };
            const grp = {
                childDrawables: [bg, circles], inert: true
            };
            const fontSize = 2, scattering = Math.ceil(anchors.length / 50);
            for (let { anchor, index } of anchors) {
                circles.pathData += ' ' + ComponentsDrawing.Drawing.PacemCircleElement.getPathData(anchor, thickness * 2.5);
                if (index % scattering === 0) {
                    const textAnchor = { x: anchor.x + fontSize * ((anchor.x - center.x) / radius), y: anchor.y + fontSize * (0.5 + ((anchor.y - center.y) / radius)) };
                    const txt = {
                        anchor: textAnchor, color: stroke, text: index.toString(), textAnchor: 'middle', fontSize
                    };
                    grp.childDrawables.push(txt);
                }
            }
            ds.push(grp);
        }
        for (let { from, to, loop, normalizedLength } of segments || []) {
            const stroke = buildStroke(loops, loop, normalizedLength);
            var pathData = ComponentsDrawing.Drawing.PacemLineElement.getPathData(from, to);
            const shape = { stroke, pathData, vertices: [from, to], lineWidth: thickness, lineCap: 'butt', opacity, inert: true };
            if (arrows) {
                shape.markerEnd = arrow(stroke, 5, memoizer);
            }
            ds.push(shape);
        }
        memoizer.clear();
        return stage.datasource = ds;
    }
}
__decorate([
    Transformer()
], VortexTransformers, "vortexDatabind", null);
